/**
 * 字段组管理Hook
 * 职责: 字段组的增删改查、标签管理、验证
 */

import type { IFieldGroup } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { fieldGroupsBasis } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import type { IBAcTag } from '@pharmsnap/shared/types'
import { checkExist, DrugFinderExpertKey, DrugFinderExpertTagKey, generateAllTag, showSingleToast, tagData } from '@pharmsnap/shared/utils'
import { computed, ref } from '@vue/composition-api'
import { cloneDeep } from 'lodash'
import { useLocale } from 'pharmsnapMF_shared/composition'
import { ALL_TAG_ID, FIELD_GROUP_MAX_COUNT, getFieldConfig, getGroupField, setGroupField } from '../config/fieldConfig'

export function useFieldGroupManager(initialGroups: IFieldGroup[]) {
  const { isCN, ts } = useLocale()
  const fieldGroups = ref<IFieldGroup[]>(cloneDeep(initialGroups))
  const customMilestoneDateItems = cloneDeep(
    fieldGroups.value.find((group) => group.fields.milestoneDate)?.fields.milestoneDate?.selectedValues || []
  )
  const customDevStatusDateItems = cloneDeep(fieldGroups.value.find((group) => group.fields.dateItems)?.fields.dateItems?.selectedValues || [])

  // ============ 计算属性 ============
  const fieldGroupCount = computed(() => fieldGroups.value.length)
  const canAddMore = computed(() => fieldGroupCount.value < FIELD_GROUP_MAX_COUNT)
  const totalFieldCount = computed(() => {
    return fieldGroups.value.reduce((acc, group) => {
      return acc + Object.keys(group.fields).length
    }, 0)
  })

  // ============ 字段组操作 ============

  /**
   * 添加字段组
   */
  const addFieldGroup = () => {
    if (!canAddMore.value) return

    const newId = (fieldGroups.value.length + 1).toString()
    const defaultFieldGroup = cloneDeep(fieldGroupsBasis.find((g) => g.property === 'drug'))

    if (defaultFieldGroup) {
      defaultFieldGroup.id = newId
      setGroupField(defaultFieldGroup, 'drugItems', { tags: [] })
      fieldGroups.value.push(defaultFieldGroup as IFieldGroup)
    }
  }

  /**
   * 删除字段组
   */
  const removeFieldGroup = (id: string) => {
    const groupIndex = fieldGroups.value.findIndex((group) => group.id === id)

    // 不能删除第一条
    if (groupIndex <= 0) return

    // 清空字段数据
    const group = fieldGroups.value[groupIndex]
    if (group) {
      ;(Object.keys(group.fields) as DrugFinderExpertKey[]).forEach((key) => {
        setGroupField(group, key, { tags: [], selectedValues: [generateAllTag()] })
      })
    }

    // 删除并重新编号
    fieldGroups.value = fieldGroups.value
      .filter((group) => group.id !== id)
      .map((group, index) => ({
        ...group,
        id: (index + 1).toString(),
      }))
  }

  /**
   * 更改字段组类型
   */
  const changeFieldGroupType = (groupId: string, newProperty: string) => {
    const groupIndex = fieldGroups.value.findIndex((g) => g.id === groupId)
    if (groupIndex === -1) return false

    const fieldGroup = fieldGroupsBasis.find((g) => g.property === newProperty)
    if (!fieldGroup) return false

    const currentGroup = fieldGroups.value[groupIndex]
    const newGroup = cloneDeep(fieldGroup)
    newGroup.id = currentGroup.id
    newGroup.logic = currentGroup.logic

    fieldGroups.value.splice(groupIndex, 1, newGroup)
  }

  // ============ 标签操作 ============

  /**
   * 添加标签
   */
  const addTag = (groupId: string, fieldKey: DrugFinderExpertKey, tag: IBAcTag | IBAcTag[]) => {
    const group = fieldGroups.value.find((g) => g.id === groupId)
    if (!group || !group.fields[fieldKey]) return

    // 初始化tags
    if (!Array.isArray(group.fields[fieldKey]?.tags)) {
      setGroupField(group, fieldKey, { tags: [] })
    }

    const tagsToAdd = Array.isArray(tag) ? tag : [tag]

    // 检查重复（仅对单个标签检查）
    if (!Array.isArray(tag)) {
      const exists = checkExist(group.fields[fieldKey]?.tags as IBAcTag[], tag, isCN.value)
      if (exists) {
        showSingleToast({
          message: ts('common.excitedItem') as string,
          type: 'info',
        })
        return
      }
    }

    // 添加不重复的标签
    tagsToAdd.forEach((item) => {
      const tags = group.fields[fieldKey]?.tags as IBAcTag[]
      if (!tags.some((t) => t.id === item.id)) {
        tags.push(item)
      }
    })
  }

  /**
   * 删除标签
   */
  const removeTag = (groupId: string, fieldKey: DrugFinderExpertKey, tagId: string) => {
    const group = fieldGroups.value.find((g) => g.id === groupId)
    if (!group || !group.fields[fieldKey] || !group.fields[fieldKey]?.tags) return

    const tags = group.fields[fieldKey]?.tags as IBAcTag[]
    setGroupField(group, fieldKey, {
      tags: tags.filter((t) => {
        return !(t.id === tagId || t.display_name_en === tagId || t.display_name_cn === tagId)
      }),
    })

    // 特殊处理: 适应症清空时恢复阶段
    if (fieldKey === 'indicationItems' && group.fields[fieldKey]?.tags?.length === 0) {
      setGroupField(group, 'indicationPhaseItems', { selectedValues: [generateAllTag()] })
    }

    // 特殊处理: 组织清空时恢复角色
    if (fieldKey === 'organizationItems' && group.fields[fieldKey]?.tags?.length === 0) {
      setGroupField(group, 'orgRoleItems', { selectedValues: [generateAllTag()] })
    }
  }

  /**
   * 切换选择值 (用于selectedValues类型的字段)
   */
  const toggleSelection = (groupId: string, fieldKey: DrugFinderExpertKey, value: IBAcTag) => {
    const group = fieldGroups.value.find((g) => g.id === groupId)
    if (group && group.fields[fieldKey]) {
      if (!group.fields[fieldKey]?.selectedValues) {
        setGroupField(group, fieldKey, { selectedValues: [] })
      }
      const index = group.fields[fieldKey]?.selectedValues?.indexOf(value) ?? -1

      if (index > -1) {
        group.fields[fieldKey]?.selectedValues?.splice(index, 1)
      } else {
        group.fields[fieldKey]?.selectedValues?.push(value)
      }
    }
  }

  /**
   * 选择标签 (处理All逻辑)
   */
  const selectTag = (group: IFieldGroup, fieldKey: DrugFinderExpertKey, item: IBAcTag, limit?: number) => {
    const selectedValues = getGroupField(group, fieldKey).selectedValues

    if (!selectedValues) return

    if (item.id === ALL_TAG_ID) {
      setGroupField(group, fieldKey, {
        selectedValues: [generateAllTag()],
      })
      return
    }

    // 检查是否已存在
    const exists = checkExist(selectedValues, item, isCN.value)
    if (exists) {
      // 移除
      setGroupField(group, fieldKey, {
        selectedValues: selectedValues.filter((o) => o.id !== item.id),
      })
    } else {
      // 添加
      setGroupField(group, fieldKey, {
        selectedValues: [...selectedValues, item],
      })
    }

    // 如果为空，恢复All
    if (!getGroupField(group, fieldKey).selectedValues?.length) {
      setGroupField(group, fieldKey, {
        selectedValues: [generateAllTag()],
      })
    } else {
      // 移除All
      setGroupField(group, fieldKey, {
        selectedValues: getGroupField(group, fieldKey).selectedValues?.filter((o) => o.id !== ALL_TAG_ID) || [],
      })
    }

    // 处理限制
    if (limit && (getGroupField(group, fieldKey).selectedValues?.length as number) > limit) {
      setGroupField(group, fieldKey, {
        selectedValues: getGroupField(group, fieldKey).selectedValues?.slice(-limit),
      })
    }

    const totalLength = tagData[fieldKey as DrugFinderExpertTagKey]?.length ?? 0
    const hasSelected = getGroupField(group, fieldKey).selectedValues?.length as number
    if (hasSelected === totalLength - 1) {
      setGroupField(group, fieldKey, {
        selectedValues: [generateAllTag()],
      })
    }
  }

  // ============ 批量操作 ============

  /**
   * 清空所有字段组
   */
  const clearAllFieldGroups = (): void => {
    fieldGroups.value.forEach((group) => {
      ;(Object.keys(group.fields) as DrugFinderExpertKey[]).forEach((key) => {
        const config = getFieldConfig(key)
        if (config?.dataType === 'selectedValues') {
          setGroupField(group, key, { selectedValues: [generateAllTag()] })
        } else {
          setGroupField(group, key, { tags: [] })
        }
      })
    })
  }

  /**
   * 重置第一组的非条件标识
   */
  const resetFirstGroupFlag = (groups: IFieldGroup[]): void => {
    groups.forEach((group) => {
      group.isNotFirstGroup = false
    })
  }

  /**
   * 更新字段组数组
   */
  const updateFieldGroups = (newGroups: IFieldGroup[]): void => {
    fieldGroups.value = cloneDeep(newGroups)
  }

  return {
    // 状态
    fieldGroups,
    fieldGroupCount,
    canAddMore,
    totalFieldCount,
    customMilestoneDateItems,
    customDevStatusDateItems,
    // 方法
    addFieldGroup,
    removeFieldGroup,
    changeFieldGroupType,
    addTag,
    removeTag,
    toggleSelection,
    selectTag,
    clearAllFieldGroups,
    resetFirstGroupFlag,
    updateFieldGroups,
  }
}

export type UseFieldGroupManagerReturn = ReturnType<typeof useFieldGroupManager>
